import { DateTime } from "luxon";
import type { Path } from "@/router";
import type { DashboardMemberSignalGroup } from "./dashboardSignal";
import type {
  CareerServicesDashboardWindow,
  MemberDashboardActivity,
  MemberDashboardActivityKey,
  MemberDashboardCelebration,
  MemberDashboardCelebrationKey,
  MemberDashboardSignal,
  MemberDashboardSignalKey
} from "../queries";
import { dashboardWindowLabel, DEFAULT_DASHBOARD_WINDOW } from "./dashboardTimeframe";

export const MEMBER_DASHBOARD_SIGNAL_KEYS: MemberDashboardSignalKey[] = [
  "RECENTLY_UNEMPLOYED",
  "UNUSUAL_APPLICATION_ACTIVITY",
  "SUSPENDED",
  "NO_INTERACTION"
];

const MEMBER_SIGNAL_GROUP_CONFIG: Record<
  MemberDashboardSignalKey,
  { id: string; label: (windowLabel: string) => string }
> = {
  RECENTLY_UNEMPLOYED: {
    id: "recently-unemployed",
    label: (windowLabel) => `RECENTLY UNEMPLOYED · ${windowLabel.toUpperCase()}`
  },
  UNUSUAL_APPLICATION_ACTIVITY: {
    id: "unusual-application-activity",
    label: () => "UNUSUAL APPLICATION ACTIVITY · 5+ APPLICATIONS / 3 WEEKS"
  },
  SUSPENDED: {
    id: "suspended-members",
    label: (windowLabel) => `SUSPENDED MEMBERS · ${windowLabel.toUpperCase()}`
  },
  NO_INTERACTION: {
    id: "no-interaction",
    label: (windowLabel) => `NO INTERACTION · ${windowLabel.toUpperCase()}`
  }
};

const MEMBER_ACTIVITY_KEYS: MemberDashboardActivityKey[] = [
  "EMPLOYMENT_CHANGES",
  "MEMBERSHIP_CLASS_CHANGES",
  "COMPENSATION_PROFILE",
  "CAREER_PROFILE"
];

const MEMBER_ACTIVITY_GROUP_CONFIG: Record<
  MemberDashboardActivityKey,
  { id: string; label: (windowLabel: string) => string }
> = {
  EMPLOYMENT_CHANGES: {
    id: "employment-changes",
    label: (windowLabel) => `EMPLOYMENT CHANGES · ${windowLabel.toUpperCase()}`
  },
  MEMBERSHIP_CLASS_CHANGES: {
    id: "membership-class-changes",
    label: (windowLabel) => `MEMBERSHIP CLASS CHANGES · ${windowLabel.toUpperCase()}`
  },
  COMPENSATION_PROFILE: {
    id: "compensation-profile",
    label: (windowLabel) => `UPDATED COMPENSATION PROFILE · ${windowLabel.toUpperCase()}`
  },
  CAREER_PROFILE: {
    id: "career-profile",
    label: (windowLabel) => `COMPLETED CAREER PROFILE · ${windowLabel.toUpperCase()}`
  }
};

const MEMBER_ACTIVITY_KIND_LABELS: Record<string, string> = {
  EMPLOYMENT_STARTED: "Employment started",
  EMPLOYMENT_CHANGED: "Employment changed",
  EMPLOYMENT_ENDED: "Employment ended",
  FACILITY_CHANGED: "Facility changed",
  TITLE_CHANGED: "Title changed",
  CLASS_CHANGED: "Class changed",
  COMPENSATION_PROFILE_UPDATED: "Updated compensation profile",
  CAREER_PROFILE_COMPLETED: "Completed career profile"
};

const MEMBER_CELEBRATION_KEYS: MemberDashboardCelebrationKey[] = [
  "ELECTIONS",
  "QUARTER_CENTURY",
  "HALF_CENTURY",
  "CERTIFICATIONS",
  "BIRTHDAYS"
];

const MEMBER_CELEBRATION_GROUP_CONFIG: Record<MemberDashboardCelebrationKey, { id: string; label: string }> = {
  ELECTIONS: { id: "elections", label: "ELECTIONS" },
  QUARTER_CENTURY: { id: "quarter-century", label: "QUARTER CENTURY" },
  HALF_CENTURY: { id: "half-century", label: "HALF CENTURY" },
  CERTIFICATIONS: { id: "certifications", label: "CERTIFICATIONS" },
  BIRTHDAYS: { id: "birthdays", label: "BIRTHDAYS" }
};

const MEMBER_CELEBRATION_KIND_LABELS: Record<string, string> = {
  ELECTED: "Elected",
  QUARTER_CENTURY: "Recognized",
  HALF_CENTURY: "Recognized",
  CERTIFICATION_EARNED: "Certification earned",
  BIRTHDAY: "Birthday"
};

function getInitials(displayName: string): string {
  const initials = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return initials || "ME";
}

function formatSignalDate(signalDate: string | null): string {
  if (!signalDate) {
    return "recently";
  }

  const parsedDate = DateTime.fromISO(signalDate);
  return parsedDate.isValid ? parsedDate.toLocaleString(DateTime.DATE_MED) : "recently";
}

function formatActivityDate(activityDate: string): string {
  const parsedDate = DateTime.fromISO(activityDate);
  if (!parsedDate.isValid) {
    return "recently";
  }

  const daysAgo = Math.max(0, Math.floor(DateTime.now().startOf("day").diff(parsedDate.startOf("day"), "days").days));
  if (daysAgo === 0) {
    return "today";
  }

  return `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;
}

function getMemberActivityLabel(activityKind: string, detail: string | null, activityDate: string): string {
  const activityDetail = detail?.trim() || MEMBER_ACTIVITY_KIND_LABELS[activityKind] || "Activity updated";
  return `${activityDetail} · ${formatActivityDate(activityDate)}`;
}

function getMemberCelebrationLabel(celebrationKind: string, detail: string | null, celebrationDate: string): string {
  const celebrationDetail = detail?.trim() || MEMBER_CELEBRATION_KIND_LABELS[celebrationKind] || "Celebration";
  const parsedDate = DateTime.fromISO(celebrationDate);
  const dateLabel = parsedDate.isValid ? parsedDate.toLocaleString(DateTime.DATE_MED) : "recently";

  return `${celebrationDetail} · ${dateLabel}`;
}

function getActivityLabel(
  signal: MemberDashboardSignalKey,
  signalDate: string | null,
  applicationCount: number | null,
  windowLabel: string
) {
  switch (signal) {
    case "RECENTLY_UNEMPLOYED":
      return `Employment ended ${formatSignalDate(signalDate)}`;
    case "UNUSUAL_APPLICATION_ACTIVITY":
      return `${applicationCount ?? 0} applications in the last 3 weeks`;
    case "SUSPENDED":
      return `Suspended ${formatSignalDate(signalDate)}`;
    case "NO_INTERACTION":
      return signalDate
        ? `No interaction since ${formatSignalDate(signalDate)}`
        : `No interaction in the ${windowLabel}`;
  }
}

function toDashboardMemberSignal(
  signal: MemberDashboardSignalKey,
  row: MemberDashboardSignal["rows"][number],
  windowLabel: string
) {
  return {
    id: row.id,
    pgaId: row.pgaId,
    name: row.displayName,
    initials: getInitials(row.displayName),
    role: row.title ?? undefined,
    facility: row.facilityName ?? undefined,
    activityLabel: getActivityLabel(signal, row.signalDate, row.applicationCount, windowLabel),
    href: row.pgaId ? (`/people/${row.pgaId}/person/overview` as Path) : null
  };
}

export function toDashboardMemberSignalGroups(
  signals: MemberDashboardSignal[],
  window: CareerServicesDashboardWindow = DEFAULT_DASHBOARD_WINDOW
): DashboardMemberSignalGroup[] {
  const signalsByKey = new Map(signals.map((signal) => [signal.key, signal]));
  const windowLabel = dashboardWindowLabel(window);

  return MEMBER_DASHBOARD_SIGNAL_KEYS.map((signalKey) => {
    const config = MEMBER_SIGNAL_GROUP_CONFIG[signalKey];
    const signal = signalsByKey.get(signalKey);

    return {
      id: config.id,
      label: config.label(windowLabel),
      count: signal?.totalCount ?? 0,
      hasMore: signal?.hasMore ?? false,
      rows: signal?.rows.map((row) => toDashboardMemberSignal(signalKey, row, windowLabel)) ?? []
    };
  });
}

function toDashboardMemberActivity(row: MemberDashboardActivity["rows"][number]) {
  return {
    id: row.id,
    pgaId: row.pgaId,
    name: row.displayName,
    initials: getInitials(row.displayName),
    role: row.title ?? undefined,
    facility: row.facilityName ?? undefined,
    activityLabel: getMemberActivityLabel(row.activityKind, row.detail, row.activityDate),
    href: row.pgaId ? (`/people/${row.pgaId}/person/overview` as Path) : null
  };
}

export function toDashboardMemberActivityGroups(
  activities: MemberDashboardActivity[],
  window: CareerServicesDashboardWindow = DEFAULT_DASHBOARD_WINDOW
): DashboardMemberSignalGroup[] {
  const activitiesByKey = new Map(activities.map((activity) => [activity.key, activity]));
  const windowLabel = dashboardWindowLabel(window);

  return MEMBER_ACTIVITY_KEYS.map((activityKey) => {
    const config = MEMBER_ACTIVITY_GROUP_CONFIG[activityKey];
    const activity = activitiesByKey.get(activityKey);

    return {
      id: config.id,
      label: config.label(windowLabel),
      count: activity?.totalCount ?? 0,
      hasMore: activity?.hasMore ?? false,
      rows: activity?.rows.map(toDashboardMemberActivity) ?? []
    };
  });
}

function toDashboardMemberCelebration(row: MemberDashboardCelebration["rows"][number]) {
  return {
    id: row.id,
    pgaId: row.pgaId,
    name: row.displayName,
    initials: getInitials(row.displayName),
    role: row.title ?? undefined,
    facility: row.facilityName ?? undefined,
    activityLabel: getMemberCelebrationLabel(row.celebrationKind, row.detail, row.celebrationDate),
    href: row.pgaId ? (`/people/${row.pgaId}/person/overview` as Path) : null
  };
}

export function toDashboardMemberCelebrationGroups(
  celebrations: MemberDashboardCelebration[]
): DashboardMemberSignalGroup[] {
  const celebrationsByKey = new Map(celebrations.map((celebration) => [celebration.key, celebration]));

  return MEMBER_CELEBRATION_KEYS.map((celebrationKey) => {
    const config = MEMBER_CELEBRATION_GROUP_CONFIG[celebrationKey];
    const celebration = celebrationsByKey.get(celebrationKey);

    return {
      id: config.id,
      label: config.label,
      count: celebration?.totalCount ?? 0,
      hasMore: celebration?.hasMore ?? false,
      rows: celebration?.rows.map(toDashboardMemberCelebration) ?? []
    };
  });
}
